import { MenuProps } from 'antd';
import { Key, ReactNode, useCallback, useContext, useMemo } from 'react';
import { PageURLs } from '@/utils/navigate';
import { Icon } from '../icons';
import { useLocation } from 'react-router-dom'; // Assuming you use react-router
// import { useAccessPermission } from '@/hooks/access-permission.hook';
import { AccessPermissionContext } from '@/hooks/access-permission.hook';
import { PERMISSION_BY_KEY } from '@/constants/permissions.constant';

type MenuItem = Required<MenuProps>['items'][number];

function getItem(
  label: ReactNode,
  key: Key,
  icon?: ReactNode,
  children?: MenuItem[]
): MenuItem {
  return {
    key, // pathname
    icon,
    children,
    label
  } as MenuItem;
}

export const MenuItems = (): MenuItem[] => {
  const permissionContext = useContext(AccessPermissionContext);
  const location = useLocation();
  const activeKey = location.pathname.split('/')[1];
  // const modules = useAccessPermission();

  const getMenuIcon = useCallback(
    (type: string, itemKey: string) => {
      const isActive = activeKey.includes(itemKey);
      const fillColor = isActive
        ? 'var(--menu-active-color)'
        : 'var(--text-color';

      return (
        <span>
          <Icon type={type} width={20} height={20} fill={fillColor} />
        </span>
      );
    },
    [activeKey]
  );

  const menuItems: MenuItem[] = useMemo(() => {
    const roleIndexes = permissionContext?.roleIndexes ?? [];

    const menus: MenuItem[] = [
      getItem(
        'Quản lý Người dùng',
        'users',
        getMenuIcon('userGroup', 'users'),
        [
          getItem('Danh sách Người dùng', PageURLs.ofUsersManagement()),
          getItem('Ghi lại hoạt động người dùng', PageURLs.ofUsersActivity())
        ]
      ),
      getItem('Quản lý Trang chủ', 'home', getMenuIcon('homeSetting', 'home'), [
        getItem('Cài đặt banner slider', PageURLs.ofHomeBannerSlider()),
        getItem('Về chúng tôi', PageURLs.ofHomeAboutUs()),
        getItem('Banner Header', PageURLs.ofHomeBannerHeader())
      ]),
      getItem(
        'Quản lý phân quyền',
        'permissions',
        getMenuIcon('permissionSetting', 'permissions'),
        [
          getItem('Quản lý quản trị viên', PageURLs.ofAdminManagement()),
          getItem('Quản lý vai trò', PageURLs.ofRoleManagement()),
          getItem('Ghi lại hoạt động admin', PageURLs.ofAdminActivityLog())
        ]
      ),
      getItem(
        'Quản lý chỉ số sức khoẻ',
        'health',
        getMenuIcon('health', 'health'),
        [
          getItem('Quản lý chuyển đổi đơn vị', PageURLs.ofUnitConversion()),
          getItem(
            'Quản lý kết luận & khuyến nghị',
            PageURLs.ofConclusionRecommendation()
          )
        ]
      ),
      getItem('Quản lý ChatGPT', 'gpt', getMenuIcon('chatGPT', 'chatGPT'), [
        // getItem('Quản lý nội dung ChatGPT', PageURLs.ofChatGPT()),
        getItem('Lịch sử trao đổi chatbot', PageURLs.ofHistoryChatBox()),
        getItem('Upload data, token', PageURLs.ofUploadKeyAndConfig())
      ]),
      getItem(
        'Quản lý tư vấn Sức khoẻ và Kiểm tra & Lên bữa ăn',
        'wellnessCare',
        getMenuIcon('wellness', 'wellnessCare'),
        [
          getItem('Quản lý thực phẩm', PageURLs.ofFoodManagement()),
          getItem('Quản lý món ăn', PageURLs.ofDishManagement()),
          getItem('Quản lý combo thực phẩm', PageURLs.ofComboFoodManagement()),
          getItem('Quản lý sản phẩm AVN', PageURLs.ofProductManagement()),
          getItem(
            'Quản lý hoạt động thể lực',
            PageURLs.ofHealthConsultingManagement()
          ),
          getItem(
            'Quản lý tình trạng sức khỏe',
            PageURLs.ofHealthStatusManagement()
          ),
          getItem(
            'Tính toán dinh dưỡng thực đơn',
            PageURLs.ofNutritionalMenuCalculatorManagement()
          ),
          getItem(
            'Quản lý tiêu chuẩn dinh dưỡng',
            PageURLs.ofNutritionStandardsManagement()
          ),
          getItem(
            'Quản lý nội dung lời khuyên & khuyến nghị',
            PageURLs.ofAdviceRecommendationManagement()
          )
        ]
      ),
      // getItem(
      //   'Quản lý thông báo',
      //   PageURLs.ofNotificationManagement(),
      //   getMenuIcon('notification', 'notification')
      // )
    ];

    const roles = roleIndexes
      .map((item) => PERMISSION_BY_KEY[item as keyof typeof PERMISSION_BY_KEY])
      .filter((item) => item !== undefined);

    const menuPermissions = menus.map((item) => {
      const menu = item as Menu;

      return {
        ...menu,
        children: menu.children
          ? menu.children.filter((mn) => roles.find((it) => it === mn.key))
          : null
      };
    });

    return menuPermissions.filter(
      (item) => item.children === null || item.children.length
    );
  }, [getMenuIcon, permissionContext?.roleIndexes]);

  return menuItems;
};
